import { Trans } from "@lingui/macro";
import React, { useEffect, useState } from "react";
import { ORDERSTATUS } from "constants/ordersStatus";
import { useInputChange } from "hooks/useInputChange";
import { useDispatch, useSelector } from "react-redux";
import { languageForUrl } from "../../../../locales/languages";
import { ISystemState } from "../../../../model/system/systemReducer";
import { IdProductsState } from "model/dashboardProducts/dProductsReducer";
import { ViewDataOrders } from "components/containers/dashboard/dataOrders/ViewComponent";
import { typePaginationDashboardObject } from "components/containers/dashboard/DashboardContentTable/PaginationDashboard/PaginationDashboard";
import {
  IdOrdersState,
  typeOrdersFilters,
} from "model/dashboardOrders/dOrdersReducer";
import {
  dOrdersSetFiltersAC,
  dOrdersSetDataSagaAC,
  dOrdersEditItemSagaAC,
  dOrdersClearFiltersAC,
  dOrdersSetPageNumberAC,
  dOrdersSetDetailItemAC,
  dOrdersExportDataSagaAC,
  dOrdersSetItemsForPageAC,
  dOrdersSetDetailItemFullSagaAC,
  dOrdersSelectedExportDataSagaAC,
} from "model/dashboardOrders/dOrdersActionCreators";

// Type
export type typeOrders = {
  variant?: string;
  setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export type typeDataOrdersRowActions = {
  id: string;
  title: JSX.Element;
  action: (id: string) => void;
}[];
export type typeDataOrdersInfoBulkActions = {
  title: JSX.Element;
  action: () => void;
}[];

// Component
export const DataOrders: React.FC<typeOrders> = ({
  variant,
  setSidebarOpen,
}) => {
  const dispatch = useDispatch();

  const dataList = useSelector(
    (state: { dOrders: IdOrdersState }) => state.dOrders.data
  );
  const lang = useSelector(
    (state: { system: ISystemState }) => state.system.language
  );
  const filters = useSelector(
    (state: { dOrders: IdOrdersState }) => state.dOrders.filters
  );
  const paginationObject = useSelector(
    (state: { dOrders: IdOrdersState }) => state.dOrders.pagination
  );
  const currentItem = useSelector(
    (state: { dOrders: IdOrdersState }) => state.dOrders.detailItem
  );
  const searchString = useSelector(
    (state: { dOrders: IdOrdersState }) => state.dOrders.filters?.searchString
  );

  const [openFilterBar, setOpenFilterBar] = useState(false);
  const [isActiveFilters, setIsActiveFilters] = useState(false);

  const pagination: typePaginationDashboardObject = {
    paginationObject: useSelector(
      (state: { dOrders: IdProductsState }) => state.dOrders.pagination
    ),
    onGoToPage: (id) => dispatch(dOrdersSetPageNumberAC(id)),
    onSelectItems: (itemsPerPage) =>
      dispatch(dOrdersSetItemsForPageAC(itemsPerPage)),
  };

  useEffect(() => {
    return () => {
      dispatch(dOrdersClearFiltersAC());
    };

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    dispatch(
      dOrdersSetDataSagaAC(filters, {
        page: pagination.paginationObject.pageNumber,
        size: pagination.paginationObject.itemsPerPage!,
      })
    );
    // eslint-disable-next-line
  }, [
    filters,
    pagination.paginationObject.pageNumber,
    pagination.paginationObject.itemsPerPage,
  ]);

  const {
    value: searchValue,
    setValue: setSearchValue,
    changeValueHandler: onChangeSearchValueHandler,
  } = useInputChange(searchString || "");

  const onClearSearch = () => {
    dispatch(dOrdersSetFiltersAC({ searchString: null }));

    setSearchValue("");
  };

  const onEnterSearch = () => {
    const filters: typeOrdersFilters = {
      searchString: searchValue.trim(),
    };

    dispatch(dOrdersSetFiltersAC(filters));
  };

  // Popup details
  const [openPopupDetails, setOpenPopupDetails] = useState(false);

  // Popup add
  const [openPopupAdd, setOpenPopupAdd] = useState(false);

  // Popup edit
  const [openPopupEdit, setOpenPopupEdit] = useState(false);

  // Popup change status
  const [openPopupChangeStatus, setOpenPopupChangeStatus] = useState(false);

  //Popup bulk change status

  const [openPopupBulkChangeStatus, setOpenPopupBulkChangeStatus] =
    useState(false);

  //Popup bulk cancel orders

  const [openPopupBulkCancel, setOpenPopupBulkCancel] = useState(false);

  // Cancel dialog
  const [openCancelDialog, setOpenCancelDialog] = useState(false);

  // Delete dialog actions
  const onCancelCancelHandler = () => {
    setOpenCancelDialog(false);
  };

  const onClickExport = () => {
    dispatch(dOrdersExportDataSagaAC(filters, languageForUrl[lang]));
  };

  const onCancelConfirmHandler = async () => {
    const dataEditObject = {
      status: ORDERSTATUS.CANCEL,
      id: currentItem!.id,
    };

    dispatch(
      // @ts-ignore
      dOrdersEditItemSagaAC(dataEditObject, filters, {
        page: paginationObject.pageNumber,
        size: paginationObject.itemsPerPage!,
      })
    );

    setOpenCancelDialog(false);
  };

  // Make table item actions
  const rowsActions = [
    {
      id: "changeStatus",
      title: <Trans>Change status</Trans>,
      action: (id: string) => {
        dispatch(dOrdersSetDetailItemAC(id));

        setOpenPopupChangeStatus(true);
      },
    },
    {
      id: "edit",
      title: <Trans>Edit</Trans>,
      action: (id: string) => {
        dispatch(dOrdersSetDetailItemAC(id));

        dispatch(dOrdersSetDetailItemFullSagaAC(id));

        setOpenPopupEdit(true);
      },
    },
    {
      id: "cancel",
      title: <Trans>Cancel</Trans>,
      action: (id: string) => {
        dispatch(dOrdersSetDetailItemAC(id));

        setOpenCancelDialog(true);
      },
    },
  ];

  // Make bulk actions
  const bulkActions: typeDataOrdersInfoBulkActions = [
    {
      title: <Trans>Selected export</Trans>,
      action: () => {
        dispatch(
          dOrdersSelectedExportDataSagaAC(
            dataList.filter((item) => item.checked).map((item) => item.id),
            languageForUrl[lang]
          )
        );
      },
    },
    {
      title: <Trans>Change status</Trans>,
      action: () => {
        setOpenPopupBulkChangeStatus(true);
      },
    },
    {
      title: <Trans>Cancel orders</Trans>,
      action: () => {
        setOpenPopupBulkCancel(true);
      },
    },
  ];

  return (
    <ViewDataOrders
      variant={variant}
      setSidebarOpen={setSidebarOpen}
      openFilterBar={openFilterBar}
      setOpenFilterBar={setOpenFilterBar}
      openPopupDetails={openPopupDetails}
      setOpenPopupDetails={setOpenPopupDetails}
      openPopupAdd={openPopupAdd}
      setOpenPopupAdd={setOpenPopupAdd}
      openPopupEdit={openPopupEdit}
      setOpenPopupEdit={setOpenPopupEdit}
      openPopupChangeStatus={openPopupChangeStatus}
      setOpenPopupChangeStatus={setOpenPopupChangeStatus}
      openPopupBulkChangeStatus={openPopupBulkChangeStatus}
      setOpenPopupBulkChangeStatus={setOpenPopupBulkChangeStatus}
      openPopupBulkCancel={openPopupBulkCancel}
      setOpenPopupBulkCancel={setOpenPopupBulkCancel}
      openCancelDialog={openCancelDialog}
      setOpenCancelDialog={setOpenCancelDialog}
      onCancelConfirmHandler={onCancelConfirmHandler}
      onCancelCancelHandler={onCancelCancelHandler}
      searchValue={searchValue}
      onChangeSearchValue={onChangeSearchValueHandler}
      onEnterSearch={onEnterSearch}
      onClearSearch={onClearSearch}
      rowsActions={rowsActions}
      bulkActions={bulkActions}
      currentItem={currentItem}
      pagination={pagination}
      isActiveFilters={isActiveFilters}
      setIsActiveFilters={setIsActiveFilters}
      onClickExport={onClickExport}
    />
  );
};
